import { useEffect } from 'react'
import { SWRConfiguration, useSWRConfig } from 'swr'

// A read with obsolete authorization or no enabled subscribers is cancelled,
// not denied by the server. Keep it out of consumer error handlers and retry
// only when an authorized subscriber returns to this cache entry.
export class RpcQueryCancelled extends Error {
  readonly name = 'RpcQueryCancelled'
  constructor() {
    super('Query cancelled because it no longer has an authorized subscriber')
  }
}

export function useQueryErrorHandler(
  config?: SWRConfiguration,
): SWRConfiguration['onError'] {
  const defaults = useSWRConfig()
  return (error, key, configuration) => {
    if (error instanceof RpcQueryCancelled) return
    const onError = config?.onError ?? defaults.onError
    onError?.(error, key, configuration)
  }
}

export function useRecoverCancelledQuery(
  key: string,
  error: unknown,
  enabled: boolean,
  isValidating: boolean,
  isCurrent: () => boolean,
  mutate: () => Promise<unknown>,
): boolean {
  const { cache } = useSWRConfig()
  const cancelled = error instanceof RpcQueryCancelled
  useEffect(() => {
    // SWR may have started revalidation in its layout effect, after this
    // render's snapshot. Read live cache state to avoid a duplicate request.
    const state = cache.get(key)
    if (
      enabled &&
      error instanceof RpcQueryCancelled &&
      !state?.isValidating &&
      isCurrent() &&
      state?.error === error
    ) {
      // This also covers warm entries with automatic revalidation disabled.
      // SWR's no-argument mutate handles fetch errors without rejecting.
      void mutate()
    }
  }, [cache, key, error, enabled, isValidating, isCurrent, mutate])
  return cancelled
}
